import React, { useMemo, useEffect } from 'react';
import IconsLayout from '@layout/UnacademyIconsLayout';
import LessonsList from '@unicons-comp/LessonsList/LessonsList';
import Dynamic from '@base/Dynamic/Dynamic';
import SEO from '@seo/index';
import styled from '@emotion/styled';
import {
  ICONS_COURSE_DETAIL,
  ICONS_COURSE_LESSONS
} from '@unicons-constants/iconsEndpoints.constants';
import useFetch from '@hooks/useFetch';
import { HOST_TYPE } from '@utils/api-helper/host';
import { NextPageContextType, PageType } from '@unicons-types/index';
import fetch from '@utils/api-helper/fetch';
import getUrl from '@utils/api-helper/getUrl';
import Header from '@unicons-comp/Course/Header/Header';
import LearningOutcomes from '@unicons-comp/Course/LearningOutcomes/LearningOutcomes';
import CourseMaterial from '@unicons-comp/Course/CourseMaterial/CourseMaterial';
import { emptyObject } from '@constants/empty';
import setupInitialProps from '@utils/setupInitialProps';
import {
  getFetcherOptions,
  getRequestUrl
} from '@utils/api-helper/getUAIconsOptions';
import { useSelector } from 'react-redux';
import { isUAIconsEnabled } from '@api/selectors/constants';
import {
  ICONS_GOAL_UID,
  UA_ICONS_SLUG
} from '@constants/unacademyIcons/icons.constants';
import { useRouter } from 'next/router';
import { mediaQuery } from '@styles/index';
import { getImage } from '@utils/imageHelper';
import Head from 'next/head';
import personSchema from '@seo/schemas/unacademyIcons/personSchema';
import { redirectToRoute } from '@utils/route';
import {
  getIsSubscribedToPlus,
  getIsSubscribedToOffline
} from '@selectors/subscription';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import useConstants from '@api/hooks/useConstants';

const SubscriptionBottomBar = Dynamic(
  () => import('@unicons-comp/SubscriptionBottomBar/SubscriptionBottomBar')
) as any;

const SEOComp = SEO as any;
interface IconsCourseProps {
  courseSlug?: string;
  courseData?: any;
}

const StyledWrapper = styled.div`
  margin-bottom: 80px;
  ${mediaQuery.uptoMobile} {
    margin-bottom: 40px;
  }
`;

const ICONS_COURSE_META_TITLE = 'Learn Cricket with Sachin on Unacademy';

const ICONS_COURSE_META_DESCRIPTION =
  'Learn the game from basics to advanced with Indian Cricketer, Sachin Tendulkar. Make him your dream cricket coach with Unacademy Icons.';

const IconsCourse: PageType<IconsCourseProps> = ({
  courseSlug,
  courseData
}) => {
  const { title: pricingTitle } = courseData?.priceDisplayInfo || emptyObject;
  const options = {
    pathVars: { course_slug: courseSlug },
    params: { limit: 100 }
  };

  const { data: lessonsData } = useFetch(
    getRequestUrl(ICONS_COURSE_LESSONS, options),
    getFetcherOptions(options),
    { revalidateOnMount: true }
  );

  const router = useRouter();
  const results = useMemo(() => lessonsData?.results, [lessonsData]);
  const { constants } = useConstants();

  const iconGoalUID = ICONS_GOAL_UID;

  const isIconsSubscribed = useSelector(getIsSubscribedToPlus(iconGoalUID));
  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(iconGoalUID)
  );

  const isIconsEnabled = isUAIconsEnabled(constants);
  useEffect(() => {
    if (!!constants && !isIconsEnabled) {
      router.push('/explore');
    }
  }, [isIconsEnabled, constants, router]);

  const hasLearningOutcomes = courseData?.learningOutcomes?.length;

  const hasMaterial = courseData?.material?.length;

  const currentUrl = `https://unacademy.com/icons/courses/${courseSlug}`;

  return (
    <>
      <SEOComp
        title={ICONS_COURSE_META_TITLE}
        url={currentUrl}
        schema={personSchema()}
        description={ICONS_COURSE_META_DESCRIPTION}
        openGraph={{
          title: ICONS_COURSE_META_TITLE,
          description: ICONS_COURSE_META_DESCRIPTION,
          images: [
            {
              url: getImage('icons-course-seo.png')
            }
          ],
          url: currentUrl
        }}
      />
      <Head>
        <link rel="shortcut icon" href={getImage('favicon-icon.ico')} />
      </Head>
      <IconsLayout showFooter>
        <StyledWrapper>
          <Header
            videoSrc={courseData?.trailer}
            coverMain={courseData?.coverMain}
            title={courseData?.title}
            titleCover={courseData?.titleCover}
            teaser={courseData?.teaser}
            courseSlug={courseSlug}
            description={courseData?.description}
            hasActiveSubscription={isIconsSubscribed || isOfflineSubscription}
            continueWatching={courseData?.continueWatching}
            isComingSoon={courseData?.isComingSoon}
            firstLessonSlug={results?.[0]?.slug}
            iconGoalUID={iconGoalUID}
            pricingInfo={courseData?.priceDisplayInfo}
          />
          {hasLearningOutcomes && (
            <LearningOutcomes learningOutcomes={courseData.learningOutcomes} />
          )}
          {hasMaterial && (
            <CourseMaterial courseMaterial={courseData.material} />
          )}
          <LessonsList
            results={results}
            hasActiveSubscription={isIconsSubscribed || isOfflineSubscription}
          />{' '}
          {!(isIconsSubscribed || isOfflineSubscription) && (
            <SubscriptionBottomBar
              pricingTitle={pricingTitle}
              pricingInfo={courseData?.priceDisplayInfo}
            />
          )}
        </StyledWrapper>
      </IconsLayout>
    </>
  );
};

IconsCourse.displayName = 'Icons Course Page';

IconsCourse.pageInfo = {
  name: 'Icons Course Page',
  isMWebSupported: true,
  layoutOptions: {
    hideIntercom: true,
    hideFreshbot: true
  }
};

IconsCourse.getInitialProps = (setupInitialProps as any)(
  async (ctx: NextPageContextType) => {
    const { query, store, isServer, res } = ctx;
    const { courseSlug } = query;

    const requestUrl = getUrl({
      endpoint: ICONS_COURSE_DETAIL,
      hostType: HOST_TYPE.UNACADEMY_ICONS,
      pathVars: { course_slug: courseSlug },
      nextPreviewMode: false
    });

    try {
      const courseResponse = await fetch(requestUrl, {
        getState: store.getState,
        dispatch: store.dispatch,
        useCamelCase: true,
        hasAppClient: true,
        useOriginalURL: true,
        hostType: HOST_TYPE.UNACADEMY_ICONS
      });
      return { courseSlug, courseData: courseResponse };
    } catch (e) {
      if (e?.response?.status === 404) {
        redirectToRoute(
          isServer,
          res,
          `/goal/${UA_ICONS_SLUG}/${ICONS_GOAL_UID}`
        );
      }

      if (e?.response?.status === 401) {
        // TODO: Move to course fallback generator
        return { courseData: {}, courseSlug };
      }
    }
  },
  IconsCourse.pageInfo
);

export default IconsCourse;

IconsCourse.Layout = ({ children }) => children;
IconsCourse.Wrapper = PageReduxProvider;
